import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';
import { useSEO } from '../../utils/seo';

const ContactPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://okayama-pet-funeral.example.com';

  useSEO({
    title: 'お問い合わせ｜岡山市のペット葬儀｜おかやまペット葬儀株式会社',
    description: 'おかやまペット葬儀株式会社へのお問い合わせ。24時間365日対応。電話086-012-3456。岡山市のペット火葬・葬儀・納骨サービス。お気軽にご相談ください。',
    keywords: '岡山市,ペット葬儀,お問い合わせ,相談,電話,24時間対応,365日対応',
    canonical: `${siteUrl}/contact`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${siteUrl}/contact#webpage`,
      url: `${siteUrl}/contact`,
      name: 'お問い合わせ',
      description: 'おかやまペット葬儀株式会社へのお問い合わせページ。24時間365日対応。',
      mainEntity: {
        '@type': 'LocalBusiness',
        name: 'おかやまペット葬儀株式会社',
        telephone: '+81-86-012-3456',
        email: 'info@okayama-pet-funeral.example.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '○○町1-2-3',
          addressLocality: '岡山市北区',
          addressRegion: '岡山県',
          postalCode: '700-0000',
          addressCountry: 'JP',
        },
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    petType: '',
    message: '',
    agree: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agree) {
      alert('個人情報保護方針に同意してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('petType', formData.petType);
      formBody.append('message', formData.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d5tcs35guoktid9sulo0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          petType: '',
          message: '',
          agree: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <PageHero title="お問い合わせ" subtitle="Contact Us" />

      {/* お問い合わせフォームセクション */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-purple-100 to-pink-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl">
            <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-gray-800 mb-3">
                お気軽にご相談ください
              </h1>
              <p className="text-sm text-gray-600">
                24時間以内にご返信いたします
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-green-800 text-sm text-center">
                  お問い合わせありがとうございます。24時間以内にご返信いたします。
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-800 text-sm text-center">
                  送信に失敗しました。お手数ですが、お電話にてお問い合わせください。
                </p>
              </div>
            )}

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* お名前 */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-base font-bold text-gray-800 mb-2"
                >
                  お名前
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="山田太郎"
                  className="w-full px-4 py-3 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all"
                />
              </div>

              {/* メールアドレス */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-base font-bold text-gray-800 mb-2"
                >
                  メールアドレス
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="example@email.com"
                  className="w-full px-4 py-3 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all"
                />
              </div>

              {/* 電話番号 */}
              <div>
                <label
                  htmlFor="phone"
                  className="block text-base font-bold text-gray-800 mb-2"
                >
                  電話番号
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="086-012-3456"
                  className="w-full px-4 py-3 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all"
                />
              </div>

              {/* ペットの種類 */}
              <div>
                <label
                  htmlFor="petType"
                  className="block text-base font-bold text-gray-800 mb-2"
                >
                  ペットの種類
                </label>
                <select
                  id="petType"
                  name="petType"
                  value={formData.petType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="犬">犬</option>
                  <option value="猫">猫</option>
                  <option value="小鳥">小鳥</option>
                  <option value="ハムスター">ハムスター</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              {/* お問い合わせ内容 */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-base font-bold text-gray-800 mb-2"
                >
                  お問い合わせ内容
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="ご質問内容をご記入ください（500文字以内）"
                  className="w-full px-4 py-3 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all resize-none"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length} / 500文字
                </p>
              </div>

              {/* プライバシーポリシー同意 */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="agree"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 text-purple-600 border-purple-300 rounded focus:ring-purple-400 cursor-pointer"
                />
                <label htmlFor="agree" className="ml-2 text-sm text-gray-700">
                  <a
                    href="/privacy"
                    className="text-purple-600 hover:text-purple-700 underline"
                  >
                    個人情報保護方針
                  </a>
                  に同意する
                </label>
              </div>

              {/* 送信ボタン */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-purple-500 text-white py-4 rounded-full text-lg font-bold hover:bg-purple-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
            </form>
          </div>

          {/* お電話でのお問い合わせ */}
          <div className="mt-12 text-center">
            <p className="text-gray-700 mb-4">お急ぎの方はお電話でお問い合わせください</p>
            <a
              href="tel:086-012-3456"
              className="inline-flex items-center bg-white text-purple-600 px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all cursor-pointer"
            >
              <i className="ri-phone-line mr-2 text-2xl"></i>
              086-012-3456
            </a>
            <p className="text-sm text-gray-600 mt-3">
              営業時間：9:00 〜 18:00（定休日：日曜日・祝日）
              <br />
              ※ 24時間365日対応可能
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
